import { useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { type PipelineRow, type TransferApplicationInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useJobs } from '../../jobs/api/jobs-api';
import { pipelineKey, transferApplication } from '../api/pipeline-api';
import styles from './ChoosePicker.module.css';
import { useStageLabels } from '../../../lib/modules';

type StartStage = TransferApplicationInput['startStage'];
const START_STAGES: readonly StartStage[] = ['screening', 'assessment', 'interview_1', 'interview_2', 'interview_3'];
const OPEN_JOBS_SHOWN = 100;

interface TransferPickerProps {
  readonly jobId: string;
  readonly row: PipelineRow;
  /** §L23: only the HR Lead starts a transfer past Screening (DECISIONS-LOG S16 Q6a). */
  readonly canSkip: boolean;
  readonly onDone: () => void;
}

/**
 * F8.15 — "must be one click or nobody will do it": pick the job and press Transfer.
 * It starts in Screening there; the later starts are shown to everyone and offered
 * only to the HR Lead, with a note, because that start is a skip.
 */
export function TransferPicker({ jobId, row, canSkip, onDone }: TransferPickerProps): ReactNode {
  const stageLabels = useStageLabels();
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const jobs = useJobs({ page: 1, pageSize: OPEN_JOBS_SHOWN, status: 'open' });
  const [targetJobId, setTargetJobId] = useState<string | null>(null);
  const [startStage, setStartStage] = useState<StartStage>('screening');
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isSkip = startStage !== 'screening';
  const problem = targetJobId === null ? 'Choose the job.' : isSkip && note.trim() === '' ? 'Say why they start past Screening.' : null;

  async function transfer(): Promise<void> {
    if (problem !== null || targetJobId === null) return;
    setIsSaving(true);
    try {
      const result = await transferApplication(row.id, { targetJobId, startStage, version: row.version, ...(isSkip ? { note: note.trim() } : {}) });
      await Promise.all([queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) }), queryClient.invalidateQueries({ queryKey: pipelineKey(result.jobId) })]);
      const target = (jobs.data?.rows ?? []).find((job) => job.id === result.jobId);
      show({ tone: 'success', message: `${row.candidate.fullName} transferred`, detail: `A new application (${result.reference}) is in ${stageLabels[result.stage]}${target ? ` on ${target.title}` : ''}. This one is kept, marked Transferred.` });
      onDone();
    } catch (error) {
      show({ tone: 'danger', message: `${row.candidate.fullName} was not transferred`, detail: errorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  }

  const options = (jobs.data?.rows ?? []).filter((job) => job.id !== jobId).map((job) => ({ value: job.id, label: job.title, description: [job.reference, job.ventureName].filter(Boolean).join(' · ') }));

  return (
    <div className={styles.picker}>
      <p className={styles.title}>
        Transfer {row.candidate.fullName}
        <span className={styles.subtitle}>A new application starts on the other job. This one stays, with its history.</span>
      </p>
      <Field label="To">{({ inputId }) => <Select id={inputId} label="Job" placeholder="Choose an open job" value={targetJobId} options={options} onChange={setTargetJobId} />}</Field>
      <Field label="Starts at" hint={canSkip ? undefined : 'Later starts are for HR.'}>
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Starts at"
            value={startStage}
            options={START_STAGES.map((stage) => ({ value: stage, label: stageLabels[stage], ...(stage !== 'screening' && !canSkip ? { disabled: true, description: 'HR only' } : {}) }))}
            onChange={(value) => setStartStage(START_STAGES.find((stage) => stage === value) ?? 'screening')}
          />
        )}
      </Field>
      {isSkip && (
        <Field label="Why they start past Screening" isRequired>
          {({ inputId }) => <Textarea id={inputId} rows={2} value={note} onChange={(event) => setNote(event.target.value)} />}
        </Field>
      )}
      <div className={styles.footer}>
        {problem !== null && <span className={styles.problem}>{problem}</span>}
        <Button variant="solid" icon={ArrowRightLeft} disabled={problem !== null} isPending={isSaving} onClick={() => void transfer()}>
          Transfer
        </Button>
      </div>
    </div>
  );
}
